/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { capturePartnerMessage } from '../utils/sentryUtils';
import {
  Box,
  Card,
  CardBody,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Text,
  VStack,
  Checkbox,
  Textarea,
  SimpleGrid,
  Radio,
  RadioGroup,
  Image,
  IconButton,
  Spinner,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { Button } from '../../../components/atoms/Button';
import { Input } from '../../../components/atoms/Input';
import { ApproveTermsModal } from './ApproveTermsModal';
import { baseUrl } from '@/service/axios';
import { IDiscountData } from '@/service/partners/api';
import { useGetCompanyDiscounts, useRegisterDiscount } from '@/service/partners/hooks';

interface StepDiscountRegistrationProps {
  onComplete: () => void;
  onPrevious: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateData: (data: any) => void;
}

export function StepDiscountRegistration({ onComplete, onPrevious, data, updateData }: StepDiscountRegistrationProps) {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<IDiscountData | null>(null);
  const [termsApproved, setTermsApproved] = useState(false);

  const toast = useToast();

  const { data: discounts, isLoading: isLoadingDiscounts } = useGetCompanyDiscounts();

  const isStaging = baseUrl?.includes('staging');

  const formSchema = z.object({
    discount: z.string().optional(),
    name: z.string().min(1, 'Título do desconto é obrigatório').max(60, 'Título deve ter no máximo 60 caracteres'),
    link: z.string().url('Link inválido').optional().or(z.literal('')),
    coupon: z.string().optional(),
    type: z.enum(['discount', 'buy_and_gain', 'treat'], {
      required_error: 'Tipo de desconto é obrigatório',
    }),
    mode: z.enum(['local', 'online'], {
      required_error: 'Modo de funcionamento é obrigatório',
    }),
    image: z.any().refine((val) => val !== undefined && val !== null, {
      message: 'Imagem é obrigatória',
    }),
    rules: z.object({
      cumulativa: z.boolean().optional(),
      delivery: z.boolean().optional(),
      promocao: z.boolean().optional(),
      disponibilidade: z.boolean().optional(),
      outros: z.boolean().optional(),
      outrosText: z.string().optional(),
    }),
    approval_ack: z.boolean().refine((val) => val === true, {
      message: 'Você deve aceitar para continuar',
    }),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    setError,
  } = useForm({
    resolver: zodResolver(
      formSchema.superRefine((data, ctx) => {
        if (data.type === 'discount') {
          if (!data.discount || data.discount.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Valor do desconto é obrigatório',
              path: ['discount'],
            });
          } else if (isNaN(Number(data.discount))) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'O valor do desconto deve ser um número válido',
              path: ['discount'],
            });
          } else if (Number(data.discount) > 100) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'O desconto percentual não pode ser maior que 100%',
              path: ['discount'],
            });
          }
        }
        if (data.mode === 'online') {
          if (!data.coupon || data.coupon.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'O campo cupom é obrigatório',
              path: ['coupon'],
            });
          }
        }
        if (data.mode === 'online' && (!data.link || data.link.length === 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Link é obrigatório para empresas online',
            path: ['link'],
          });
        }
        if (!data.image) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Imagem é obrigatória',
            path: ['image'],
          });
        }
      })
    ),
    defaultValues: {
      discount: data?.discount?.discount || '',
      name: data?.discount?.name || '',
      coupon: data?.discount?.coupon || '',
      type: data?.discount?.type || 'discount',
      mode: data?.companyMode || 'local',
      link: data?.discount?.link || '',
      rules: {
        cumulativa: data?.discount?.rules?.cumulativa || false,
        delivery: data?.discount?.rules?.delivery || false,
        promocao: data?.discount?.rules?.promocao || false,
        disponibilidade: data?.discount?.rules?.disponibilidade || false,
        outros: data?.discount?.rules?.outros || false,
        outrosText: data?.discount?.rules?.outrosText || '',
      },
      approval_ack: false,
    },
  });

  const watchedType = watch('type');
  const watchedMode = watch('mode');

  // Hook da API
  const registerDiscount = useRegisterDiscount({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (response: any) => {
      updateData({
        discount: {
          ...data.discount,
          ...response,
        },
      });
      setTermsApproved(false);
      onComplete();
    },
    onError: (error: string) => {
      // Capturar erro no Sentry
      capturePartnerMessage(`Discount registration error in StepDiscountRegistration: ${error}`, {
        component: 'StepDiscountRegistration',
        step: 'discount_registration',
        action: 'register_discount',
      });
      
      toast({
        title: 'Erro ao registrar desconto',
        description: error,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      setTermsApproved(true); // Mantém aprovado, mas não avança
    },
  });

  // Lidar com upload de imagem
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar formato do arquivo (somente imagens JPEG, PNG ou WebP)
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Formato inválido. Envie uma imagem JPEG, PNG ou WebP.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        e.target.value = ''; // Limpar o input
        return;
      }

      // Validar tamanho do arquivo (2MB = 2 * 1024 * 1024 bytes)
      const maxSize = 2 * 1024 * 1024; // 2MB em bytes
      if (file.size > maxSize) {
        toast({
          title: 'O arquivo é muito grande. O tamanho máximo permitido é 2MB.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        e.target.value = ''; // Limpar o input
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (setValue as any)('image', file);

      // Preview da imagem
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (setValue as any)('image', undefined);
    setImagePreview(null);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (formData: any) => {
    setPendingFormData(formData);

    if (termsApproved) {
      handleAcceptTerms(formData);
    } else {
      setIsTermsModalOpen(true);
    }
  };

  const handleAcceptTerms = (formData?: any) => {
    setIsTermsModalOpen(false);
    setTermsApproved(true);

    const discountDataAux: any = formData || pendingFormData;
    // Verificar se o valor do desconto é um número válido
    if (discountDataAux?.discount && isNaN(Number(discountDataAux.discount))) {
      setError('discount', {
        message: 'O valor do desconto deve ser um número válido',
      });
      return;
    }

    if (discountDataAux?.type === 'discount') {
      if (Number(discountDataAux?.discount) > 100) {
        setError('discount', {
          message: 'O desconto percentual não pode ser maior que 100%',
        });
        return;
      }
    }

    if (discountDataAux) {
      let rules = '';

      if (typeof discountDataAux.rules !== 'string') {
        if (discountDataAux.rules.cumulativa) {
          rules += 'Não cumulativa com outras promoções.\n';
        }
        if (discountDataAux.rules.delivery) {
          rules += 'Exceto Delivery.\n';
        }
        if (discountDataAux.rules.promocao) {
          rules += 'Promoção válida somente para essa unidade.\n';
        }
        if (discountDataAux.rules.disponibilidade) {
          rules += 'Sujeito a disponibilidade do produto e lotação do estabelecimento.\n';
        }
        if (discountDataAux.rules.outros) {
          rules += `\n\n${discountDataAux.rules.outrosText || ''}`;
        }
      }
      const discountData: IDiscountData = {
        discount: discountDataAux.discount || 0,
        name: discountDataAux.name,
        link: discountDataAux.link,
        coupon: discountDataAux.coupon || '',
        type: discountDataAux.type,
        image: discountDataAux.image,
        rules: rules,
        company: data?.company?.id || 0,
        stores: data?.store?.id || 0,
        mode: discountDataAux.mode,
      };

      registerDiscount.mutate(discountData);
    }
  };

  const renderDiscountTypeHelp = () => {
    switch (watchedType) {
      case 'discount':
        return (
          <Box bg="blue.50" p={3} borderRadius="md">
            <Text fontSize="sm" fontWeight="medium" mb={1} color="blue.800">
              Desconto percentual:
            </Text>
            <VStack align="start" spacing={1}>
              <Text fontSize="xs" color="blue.700">
                • Digite apenas o número (ex: 15 para 15%)
              </Text>
              <Text fontSize="xs" color="blue.700">
                • Máximo de 100%
              </Text>
              <Text fontSize="xs" color="blue.700">
                • Ideal para promoções sazonais
              </Text>
            </VStack>
          </Box>
        );
      case 'buy_and_gain':
        return (
          <Box bg="green.50" p={3} borderRadius="md">
            <Text fontSize="sm" fontWeight="medium" mb={1} color="green.800">
              Compre e Ganhe:
            </Text>
            <VStack align="start" spacing={1}>
              <Text fontSize="xs" color="green.700">
                • Oferta do tipo "compre X e ganhe Y"
              </Text>
              <Text fontSize="xs" color="green.700">
                • Ex: Compre 1 e ganhe outro
              </Text>
              <Text fontSize="xs" color="green.700">
                • Ideal para aumentar volume de vendas
              </Text>
            </VStack>
          </Box>
        );
      case 'treat':
        return (
          <Box bg="purple.50" p={3} borderRadius="md">
            <Text fontSize="sm" fontWeight="medium" mb={1} color="purple.800">
              Brinde:
            </Text>
            <VStack align="start" spacing={1}>
              <Text fontSize="xs" color="purple.700">
                • Oferta de brinde gratuito
              </Text>
              <Text fontSize="xs" color="purple.700">
                • Ex: Ganhe um brinde na compra
              </Text>
              <Text fontSize="xs" color="purple.700">
                • Ideal para fidelizar clientes
              </Text>
            </VStack>
          </Box>
        );
      default:
        return null;
    }
  };

  if (isLoadingDiscounts) {
    return (
      <Container width={'100%'}>
        <VStack spacing={6}>
          <Box textAlign="center">
            <Spinner size="xl" color="blue.500" />
          </Box>
        </VStack>
      </Container>
    );
  }

  if (discounts && discounts.length > 0) {
    return (
      <Container width={'100%'}>
        <VStack spacing={6}>
          <Box textAlign="center">
            <Heading size="lg" color="gray.800" mb={2}>
              Descontos Cadastrados
            </Heading>
            <Text color="gray.600">
              Você já possui descontos cadastrados. Para adicionar um novo desconto, clique no botão abaixo.
            </Text>
          </Box>
        </VStack>

        <VStack spacing={3} w="full">
          <Button
            colorScheme="blue"
            variant="outline"
            size="lg"
            w="full"
            onClick={() =>
              window.open(`https://pdv.${isStaging ? 'beta.' : ''}partiu.com.br/login/${data.token}`, '_blank')
            }
          >
            🔧 Acessar PDV para Editar Dados
          </Button>

          <Button
            type="button"
            variant="label"
            onClick={onPrevious}
            flex={1}
            color={'#5e5e5b'}
            border="none"
            backgroundColor="transparent"
          >
            Voltar
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <Container width={'100%'}>
      <VStack spacing={6}>
        <Box textAlign="center">
          <Heading size="lg" color="gray.800" mb={2}>
            Configurar Desconto
          </Heading>
          <Text color="gray.600">Configure a oferta que será exibida para os usuários</Text>
        </Box>

        <Card w="full" shadow="lg" p={4}>
          <CardBody>
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={6}>
                {/* Tipo de Desconto */}
                <Box w="full">
                  <Heading size="md" color="gray.800" mb={4}>
                    🎯 Tipo de Desconto
                  </Heading>

                  <FormControl mb={4}>
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup value={field.value} onChange={field.onChange}>
                          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                            <Box
                              p={4}
                              border="1px"
                              borderColor="gray.200"
                              borderRadius="lg"
                              cursor="pointer"
                              _hover={{ bg: 'gray.50' }}
                            >
                              <Radio value="discount" colorScheme="blue">
                                <Box ml={3}>
                                  <Text fontWeight="medium">Percentual</Text>
                                  <Text fontSize="sm" color="gray.600">
                                    Ex: 15% de desconto
                                  </Text>
                                </Box>
                              </Radio>
                            </Box>

                            <Box
                              p={4}
                              border="1px"
                              borderColor="gray.200"
                              borderRadius="lg"
                              cursor="pointer"
                              _hover={{ bg: 'gray.50' }}
                            >
                              <Radio value="buy_and_gain" colorScheme="blue">
                                <Box ml={3}>
                                  <Text fontWeight="medium">Compre e Ganhe</Text>
                                  <Text fontSize="sm" color="gray.600">
                                    Ex: Compre 1 e ganhe outro
                                  </Text>
                                </Box>
                              </Radio>
                            </Box>

                            <Box
                              p={4}
                              border="1px"
                              borderColor="gray.200"
                              borderRadius="lg"
                              cursor="pointer"
                              _hover={{ bg: 'gray.50' }}
                            >
                              <Radio value="treat" colorScheme="blue">
                                <Box ml={3}>
                                  <Text fontWeight="medium">Brinde</Text>
                                  <Text fontSize="sm" color="gray.600">
                                    Ex: Ganhe um brinde
                                  </Text>
                                </Box>
                              </Radio>
                            </Box>
                          </SimpleGrid>
                        </RadioGroup>
                      )}
                    />
                  </FormControl>

                  {renderDiscountTypeHelp()}
                </Box>

                {/* Modo de Funcionamento */}
                {!data?.companyMode && (
                  <Box w="full">
                    <Heading size="md" color="gray.800" mb={4}>
                      🏢 Modo de Funcionamento
                    </Heading>

                    <VStack spacing={4} w="full">
                      <FormControl isInvalid={!!errors.mode} w="full">
                        <FormLabel fontWeight="medium" color="gray.700" mb={3}>
                          Como sua empresa funciona?
                        </FormLabel>
                        <Controller
                          name="mode"
                          control={control}
                          render={({ field }) => (
                            <Stack direction={{ base: 'column', md: 'row' }} spacing={6} w="full">
                              {[
                                {
                                  value: 'local',
                                  icon: '🏪',
                                  title: 'Loja Física',
                                  desc: 'Empresa com endereço físico para atendimento presencial',
                                },
                                {
                                  value: 'online',
                                  icon: '🌐',
                                  title: 'Online',
                                  desc: 'Empresa digital sem endereço físico',
                                },
                              ].map((option) => (
                                <Box
                                  key={option.value}
                                  as="button"
                                  type="button"
                                  onClick={() => field.onChange(option.value)}
                                  p={4}
                                  border="2px solid"
                                  borderColor={field.value === option.value ? 'blue.500' : 'gray.200'}
                                  borderRadius="lg"
                                  bg={field.value === option.value ? 'blue.50' : 'white'}
                                  boxShadow={field.value === option.value ? 'md' : 'none'}
                                  transition="all 0.2s"
                                  minH="120px"
                                  w="full"
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                  justifyContent="center"
                                  _hover={{
                                    borderColor: 'blue.300',
                                    bg: field.value === option.value ? 'blue.50' : 'gray.50',
                                  }}
                                  cursor="pointer"
                                >
                                  <Box fontSize="2xl" color={field.value === option.value ? 'blue.500' : 'gray.600'}>
                                    {option.icon}
                                  </Box>
                                  <Text fontWeight="semibold" color="gray.800" mt={2}>
                                    {option.title}
                                  </Text>
                                  <Text fontSize="sm" color="gray.600" textAlign="center" mt={1}>
                                    {option.desc}
                                  </Text>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        />
                      </FormControl>
                    </VStack>
                  </Box>
                )}

                {/* Dados do Desconto */}
                <Box w="full">
                  <Heading size="md" color="gray.800" mb={4}>
                    📝 Dados do Desconto
                  </Heading>

                  <VStack spacing={4}>
                    {watchedType === 'discount' && (
                      <FormControl isInvalid={!!errors.discount}>
                        <Input
                          type="number"
                          placeholder="15 (para 15%)"
                          label="Valor do Desconto (%)"
                          name="discount"
                          control={control}
                        />
                      </FormControl>
                    )}

                    <FormControl isInvalid={!!errors.name}>
                      <Input
                        type="text"
                        placeholder="Título da oferta (ex: 15% OFF em toda loja)"
                        label="Título da Oferta"
                        name="name"
                        control={control}
                      />
                    </FormControl>

                    {watchedMode === 'online' && (
                      <FormControl isInvalid={!!(errors as any).coupon}>
                        <Input
                          type="text"
                          placeholder="Código do cupom"
                          label="Código do Cupom"
                          name="coupon"
                          control={control}
                        />
                      </FormControl>
                    )}

                    {watchedMode === 'online' && (
                      <FormControl isInvalid={!!errors.link}>
                        <Input
                          type="text"
                          placeholder="Link da oferta"
                          label="Link da Oferta"
                          name="link"
                          control={control}
                        />
                      </FormControl>
                    )}
                  </VStack>
                </Box>

                {/* Imagem do Desconto */}
                <Box w="full">
                  <Heading size="md" color="gray.800" mb={4}>
                    🖼️ Imagem da Oferta
                  </Heading>

                  <VStack spacing={4} align="start">
                    <Box>
                      <label htmlFor="discount-image-upload">
                        <Button colorScheme="blue" variant="outline" cursor="pointer" as="span">
                          {imagePreview ? 'Trocar imagem' : 'Enviar imagem'}
                        </Button>
                      </label>
                      <input
                        id="discount-image-upload"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                    </Box>

                    {imagePreview && (
                      <Box position="relative" display="inline-block">
                        <Image src={imagePreview} alt="Preview" w="32" h="32" objectFit="cover" borderRadius="lg" />
                        <IconButton
                          size="xs"
                          colorScheme="red"
                          position="absolute"
                          top={-2}
                          right={-2}
                          borderRadius="full"
                          onClick={removeImage}
                          aria-label="Remover imagem"
                          icon={<CloseIcon />}
                        />
                      </Box>
                    )}

                    <Text fontSize="sm" color="gray.600">
                      Tamanho máximo: 2MB. Formatos: JPEG, PNG ou WebP
                    </Text>
                    <Text color="gray.500" fontSize="xs" whiteSpace="normal" textAlign="center">
                      Tamanho recomendado: 250px x 250px
                    </Text>

                    {(errors as any).image && (
                      <Text color="red.500" fontSize="sm">
                        {(errors as any).image.message}
                      </Text>
                    )}
                  </VStack>
                </Box>

                {/* Regras do Desconto */}
                <Box w="full">
                  <Heading size="md" color="gray.800" mb={4}>
                    📋 Regras do Desconto
                  </Heading>

                  <VStack spacing={4} align="start">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                      <Controller
                        name="rules.cumulativa"
                        control={control}
                        render={({ field }) => (
                          <Checkbox isChecked={field.value} onChange={field.onChange} colorScheme="blue">
                            Não cumulativa com outras promoções.
                          </Checkbox>
                        )}
                      />

                      <Controller
                        name="rules.delivery"
                        control={control}
                        render={({ field }) => (
                          <Checkbox isChecked={field.value} onChange={field.onChange} colorScheme="blue">
                            Exceto Delivery.
                          </Checkbox>
                        )}
                      />

                      <Controller
                        name="rules.promocao"
                        control={control}
                        render={({ field }) => (
                          <Checkbox isChecked={field.value} onChange={field.onChange} colorScheme="blue">
                            Promoção válida somente para essa unidade.
                          </Checkbox>
                        )}
                      />

                      <Controller
                        name="rules.disponibilidade"
                        control={control}
                        render={({ field }) => (
                          <Checkbox isChecked={field.value} onChange={field.onChange} colorScheme="blue">
                            Sujeito a disponibilidade do produto e lotação do estabelecimento.
                          </Checkbox>
                        )}
                      />

                      <Controller
                        name="rules.outros"
                        control={control}
                        render={({ field }) => (
                          <Checkbox isChecked={field.value} onChange={field.onChange} colorScheme="blue">
                            Outras condições
                          </Checkbox>
                        )}
                      />
                    </SimpleGrid>

                    {watch('rules.outros') && (
                      <FormControl isInvalid={!!errors.rules?.outrosText}>
                        <Controller
                          name="rules.outrosText"
                          control={control}
                          render={({ field }) => (
                            <Textarea
                              placeholder="Descreva outras regras ou condições específicas..."
                              {...field}
                              minH="80px"
                              borderColor="gray.300"
                              _focus={{
                                borderColor: 'blue.500',
                                boxShadow: '0 0 0 1px #3182ce',
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    )}
                  </VStack>
                </Box>

                <FormControl isInvalid={!!errors.approval_ack}>
                  <Controller
                    name="approval_ack"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        isChecked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        colorScheme="blue"
                      >
                        Entendo que o desconto só será disponibilizado quando aprovado pela {data?.company?.name}
                      </Checkbox>
                    )}
                  />
                  {errors.approval_ack && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.approval_ack.message}
                    </Text>
                  )}
                </FormControl>

                <Button type="submit" isLoading={registerDiscount.isLoading} loadingText="Finalizando..." flex={1}>
                  Finalizar Cadastro
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onPrevious}
                  flex={1}
                  color={'#5e5e5b'}
                  border="none"
                  backgroundColor="white"
                >
                  Voltar
                </Button>
              </VStack>
            </form>
          </CardBody>
        </Card>
        <ApproveTermsModal
          isOpen={isTermsModalOpen}
          onClose={() => setIsTermsModalOpen(false)}
          onAccept={handleAcceptTerms}
          company={data?.company}
          store={data?.store}
          discount={pendingFormData || null}
        />
      </VStack>
    </Container>
  );
}
